import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3208';
import {
  doDeleteSource,
  doListSources,
  doReindexCorpus,
  doReindexSource,
  doUploadSource,
  luHandleAppiError,
  u3208ParseSourcesList,
  u3208ParseU2200JobResponse,
  u3208ParseUploadResponse,
  type U3208DocSource,
  type U3208U2200Job,
} from '../U3208Service';

export function useU3208SourcesHandlers(corpusId: string | undefined) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const [sources, setSources] = useState<U3208DocSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadSources = useCallback(async () => {
    if (!corpusId || corpusId === 'new') {
      setSources([]);
      return;
    }
    setLoading(true);
    try {
      const response = await doListSources(axios, corpusId);
      setSources(u3208ParseSourcesList(response.data));
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    } finally {
      setLoading(false);
    }
  }, [axios, corpusId, t, toast]);

  const uploadSource = useCallback(
    async (formData: FormData): Promise<{ source: U3208DocSource; job: U3208U2200Job } | null> => {
      if (!corpusId || corpusId === 'new') return null;
      setSubmitting(true);
      try {
        const response = await doUploadSource(axios, corpusId, formData);
        const parsed = u3208ParseUploadResponse(response.data);
        if (!parsed) {
          toast.error(t('toast.jobFailed', { ns: NS }), t('errorTitle', { ns: NS }));
          return null;
        }
        return parsed;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [axios, corpusId, t, toast],
  );

  const deleteSource = useCallback(
    async (sourceId: string): Promise<boolean> => {
      setSubmitting(true);
      try {
        await doDeleteSource(axios, sourceId);
        setSources((prev) => prev.filter((s) => s.id !== sourceId));
        toast.success(t('toast.deleteOk', { ns: NS }));
        return true;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [axios, t, toast],
  );

  const reindexSource = useCallback(
    async (sourceId: string): Promise<U3208U2200Job | null> => {
      setSubmitting(true);
      try {
        const response = await doReindexSource(axios, sourceId);
        return u3208ParseU2200JobResponse(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [axios, t, toast],
  );

  const reindexCorpus = useCallback(async (): Promise<U3208U2200Job | null> => {
    if (!corpusId || corpusId === 'new') return null;
    setSubmitting(true);
    try {
      const response = await doReindexCorpus(axios, corpusId);
      return u3208ParseU2200JobResponse(response.data);
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [axios, corpusId, t, toast]);

  return {
    sources,
    setSources,
    loading,
    submitting,
    loadSources,
    uploadSource,
    deleteSource,
    reindexSource,
    reindexCorpus,
  };
}
